import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  type UseModalProps,
} from "@chakra-ui/react";
import { FormRow, Select } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import {
  AvailableWorkTypesForBulkMovingTimeTrackingItemsDocument,
  type AvailableWorkTypesForBulkMovingTimeTrackingItemsQuery,
  type AvailableWorkTypesForBulkMovingTimeTrackingItemsQueryVariables,
  CheckTimeTrackingItemsLockedDocument,
  type CheckTimeTrackingItemsLockedQuery,
  type CheckTimeTrackingItemsLockedQueryVariables,
  MoveTimeTrackingItemsToWorkTypeDocument,
  type MoveTimeTrackingItemsToWorkTypeMutation,
  type MoveTimeTrackingItemsToWorkTypeMutationVariables,
} from "@src/__generated__/graphql";
import type { IOption } from "@src/components/ui-kit";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import { useStore } from "@src/utils/hooks";
import mapToOptions from "@src/utils/map-to-options";
import { action, computed, makeObservable, observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

interface MoveHoursToPositionModalProps extends UseModalProps {
  itemIds: string[];
  onMoveCompleted: () => void;
}

class MoveHoursToPositionStore {
  @observable.ref itemIds: string[] = [];
  @observable.ref workTypeOptions: IOption[] = [];
  @observable selectedWorkType: string | undefined = undefined;

  private workTypesFetcher = new FetchHelper<
    AvailableWorkTypesForBulkMovingTimeTrackingItemsQuery,
    AvailableWorkTypesForBulkMovingTimeTrackingItemsQueryVariables
  >(AvailableWorkTypesForBulkMovingTimeTrackingItemsDocument);

  private moveMutator = new MutationHelper<
    MoveTimeTrackingItemsToWorkTypeMutation,
    MoveTimeTrackingItemsToWorkTypeMutationVariables
  >(MoveTimeTrackingItemsToWorkTypeDocument);

  private checkLockedFetcher = new FetchHelper<
    CheckTimeTrackingItemsLockedQuery,
    CheckTimeTrackingItemsLockedQueryVariables
  >(CheckTimeTrackingItemsLockedDocument);

  constructor(private appStore: AppStore) {
    makeObservable(this);
  }

  @computed
  get isOptionsLoading(): boolean {
    return this.workTypesFetcher.isLoading.value;
  }

  @computed
  get isMoveLoading(): boolean {
    return (
      this.checkLockedFetcher.isLoading.value ||
      this.moveMutator.isLoading.value
    );
  }

  @action.bound
  setItemIds(itemIds: string[]): void {
    this.itemIds = itemIds;
    this.selectedWorkType = undefined;
  }

  @action.bound
  setSelectedWorkType(value: string | undefined): void {
    this.selectedWorkType = value;
  }

  @action.bound
  async fetchWorkTypeOptions(isOpen: boolean): Promise<void> {
    if (!isOpen || this.itemIds.length === 0) {
      this.workTypeOptions = [];
      return;
    }

    const [data, error] = await this.workTypesFetcher.fetch({
      itemIds: this.itemIds,
    });

    if (error || !data?.getAvailableWorkTypesForBulkMovingTimeTrackingItems) {
      this.workTypeOptions = [];
      return;
    }

    this.workTypeOptions = mapToOptions.toOptions(
      data.getAvailableWorkTypesForBulkMovingTimeTrackingItems,
      "title",
    );
  }

  @action.bound
  async getUnlockedItemIds(ids: string[]): Promise<string[]> {
    if (ids.length === 0) return [];

    const [data] = await this.checkLockedFetcher.fetch({ itemIds: ids });

    const lockedIds = new Set(data?.checkTimeTrackingItemsLocked ?? []);

    if (lockedIds.size === 0) return ids;

    const unlockedIds = ids.filter((id) => !lockedIds.has(id));

    this.appStore.UIStore.toast({
      status: "info",
      title: t`${ids.length - unlockedIds.length} items are locked and changes won't be applied.`,
    });

    return unlockedIds;
  }

  @action.bound
  async move(onClose: () => void, onMoveCompleted: () => void): Promise<void> {
    if (!this.selectedWorkType) return;

    const unlockedIds = await this.getUnlockedItemIds(this.itemIds);

    if (unlockedIds.length === 0) return;

    const [data, error] = await this.moveMutator.mutate({
      work_type_id: this.selectedWorkType,
      item_ids: unlockedIds,
    });

    if (error || !data?.moveTimeTrackingItemsToWorkType) return;

    this.appStore.UIStore.toast({
      title: t`${data.moveTimeTrackingItemsToWorkType.successful} items have been moved`,
      status: "success",
    });
    onClose();
    onMoveCompleted();
  }
}

const MoveHoursToPositionModal = ({
  itemIds,
  onMoveCompleted,
  ...props
}: MoveHoursToPositionModalProps) => {
  const appStore = useStore();
  const [store] = useState(() => new MoveHoursToPositionStore(appStore));

  useEffect(() => {
    store.setItemIds(itemIds);
  }, [itemIds, store]);

  useEffect(() => {
    store.fetchWorkTypeOptions(Boolean(props.isOpen));
  }, [props.isOpen, itemIds, store]);

  const title = t`Move ${appStore.workspaceStore
    .settings!.timeTrackingRateTypeReadable()
    .toLowerCase()} to another position`;

  return (
    <Modal isCentered {...props} trapFocus={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormRow title={t`Position`}>
            <Select
              asPortal
              placeholder={t`Select position`}
              isLoading={store.isOptionsLoading}
              onChange={store.setSelectedWorkType}
              value={store.selectedWorkType}
              options={store.workTypeOptions}
            />
          </FormRow>
        </ModalBody>
        <ModalFooter gap="2">
          <Button onClick={() => props.onClose()} variant="outline">
            <Trans>Cancel</Trans>
          </Button>
          <Button
            isDisabled={!store.selectedWorkType}
            isLoading={store.isMoveLoading}
            onClick={() => store.move(props.onClose, onMoveCompleted)}
          >
            <Trans>Save</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default observer(MoveHoursToPositionModal);
